/**
 * Owner-readable view of one Supervisor AI perspective (#630).
 *
 * The node publishes each perspective on the existing report stream as a
 * `supervisor_ai_interpretation` envelope. History used to show only its one-line
 * summary, so the recommendation, idea, Unknowns and what actually ran were not
 * visible on the web. This reads that same envelope through the shared validator;
 * nothing is fetched, inferred or filled from configuration.
 */
import {
  isSupervisorAiEnvelope, supervisorAiInterpretationFromEnvelope, type SupervisorAiInterpretation,
} from '@brunel/shared';

export interface SupervisorAiPerspectiveView {
  readonly roleTitle: string;
  readonly confidence: string;
  readonly interpretation: string;
  readonly lines: readonly { readonly label: string; readonly text: string }[];
  /** Execution-derived Actual as the node recorded it. */
  readonly actual: string;
  readonly playbookVersion: string;
}

export type SupervisorAiPerspectiveRead =
  | { readonly kind: 'not_perspective' }
  | { readonly kind: 'unreadable' }
  | { readonly kind: 'perspective'; readonly view: SupervisorAiPerspectiveView };

const MANAGEMENT_TITLES: Readonly<Record<string, string>> = {
  overseer: 'Overseer', observer_ux: 'Observer UX', observer_core: 'Observer Core',
};

/** The Supervisors no longer run. Their names stay so their past reports can be read. */
const SUPERVISOR_TITLES: Readonly<Record<string, string>> = {
  machinist: 'Machinist', watchdog: 'Watchdog', project_keeper: 'Project Keeper',
  qa_reviewer: 'QA Reviewer', research_news: 'Research / News',
};

function supervisorRoleTitle(role: string): string {
  return SUPERVISOR_TITLES[role] ?? role;
}

function reviewRoleTitle(role: string): string {
  return MANAGEMENT_TITLES[role] ?? supervisorRoleTitle(role);
}

export function readSupervisorAiPerspective(envelope: unknown): SupervisorAiPerspectiveRead {
  if (!isSupervisorAiEnvelope(envelope)) return { kind: 'not_perspective' };
  let item: SupervisorAiInterpretation;
  try {
    item = supervisorAiInterpretationFromEnvelope(envelope);
  } catch {
    return { kind: 'unreadable' };
  }
  const lines: { label: string; text: string }[] = [];
  const add = (label: string, text: string | null) => { if (text !== null) lines.push({ label, text }); };
  add('Concern', item.concern);
  add('Recommendation', item.recommendation);
  for (const alternative of item.alternatives) add('Alternative', alternative);
  add('Idea', item.ideaOpportunity);
  add('Next proof or action', item.proposedNextProofOrAction);
  for (const unknown of item.unknowns) add('Unknown', unknown);
  for (const request of item.requestRoleReview) add(`Asks ${reviewRoleTitle(request.role)} to review`, request.reason);
  const run = item.execution;
  const actual = [
    run.runtimeKey,
    run.modelKey ?? 'model Unknown',
    `effort ${run.effortKey ?? 'Unknown'}`,
    run.via === 'fallback' ? 'configured fallback' : 'primary',
    run.verification === 'matched' ? 'matched' : 'partially Unknown',
  ].join(' · ');
  return {
    kind: 'perspective',
    view: {
      roleTitle: supervisorRoleTitle(item.role),
      confidence: item.confidence,
      interpretation: item.interpretation,
      lines,
      actual,
      playbookVersion: item.playbookVersion ?? 'Unknown',
    },
  };
}
